import * as THREE from 'three';
import { wearJewellery } from './jewellery';
import { wearHairdo } from './hairdo';
import type { Human } from './model';

/**
 * Rigging for the parametric human: builds MakeHuman's default skeleton fitted to the current
 * shape, skins the body and its proxies, and retargets Rocketbox (3ds Max Biped) clips onto it.
 *
 * Bone names have '.' replaced with '_' (upperarm01.L -> upperarm01_L) because three's
 * PropertyBinding treats '.' as a path separator in track names.
 */

const safe = (n: string) => n.replace(/\./g, '_');

/** MakeHuman bone (safe name) -> Rocketbox bone. */
export const RETARGET_MAP: Record<string, string> = {
  root: 'Bip01 Pelvis', spine05: 'Bip01 Spine', spine03: 'Bip01 Spine1', spine01: 'Bip01 Spine2',
  neck01: 'Bip01 Neck', head: 'Bip01 Head',
  ...side('L'), ...side('R'),
};

function side(s: 'L' | 'R'): Record<string, string> {
  const m: Record<string, string> = {
    [`clavicle_${s}`]: `Bip01 ${s} Clavicle`, [`upperarm01_${s}`]: `Bip01 ${s} UpperArm`,
    [`lowerarm01_${s}`]: `Bip01 ${s} Forearm`, [`wrist_${s}`]: `Bip01 ${s} Hand`,
    [`upperleg01_${s}`]: `Bip01 ${s} Thigh`, [`lowerleg01_${s}`]: `Bip01 ${s} Calf`,
    [`foot_${s}`]: `Bip01 ${s} Foot`, [`toe1-1_${s}`]: `Bip01 ${s} Toe0`,
  };
  // fingers: MakeHuman finger1 = thumb ... finger5 = little; Rocketbox Finger0 = thumb, segments '', '1', '2'
  for (let f = 1; f <= 5; f++) for (let k = 1; k <= 3; k++) m[`finger${f}-${k}_${s}`] = `Bip01 ${s} Finger${f - 1}${k === 1 ? '' : k - 1}`;
  return m;
}

/** Direction partner for each mapped bone: [target child, source child] used to align rest poses. */
const DIRECTION: Record<string, [string, string]> = {
  root: ['spine05', 'Bip01 Spine'], spine05: ['spine03', 'Bip01 Spine1'], spine03: ['spine01', 'Bip01 Spine2'],
  spine01: ['neck01', 'Bip01 Neck'], neck01: ['head', 'Bip01 Head'],
  ...dirSide('L'), ...dirSide('R'),
};
function dirSide(s: 'L' | 'R'): Record<string, [string, string]> {
  const d: Record<string, [string, string]> = {
    [`clavicle_${s}`]: [`upperarm01_${s}`, `Bip01 ${s} UpperArm`],
    [`upperarm01_${s}`]: [`lowerarm01_${s}`, `Bip01 ${s} Forearm`],
    [`lowerarm01_${s}`]: [`wrist_${s}`, `Bip01 ${s} Hand`],
    [`wrist_${s}`]: [`finger3-1_${s}`, `Bip01 ${s} Finger2`],
    [`upperleg01_${s}`]: [`lowerleg01_${s}`, `Bip01 ${s} Calf`],
    [`lowerleg01_${s}`]: [`foot_${s}`, `Bip01 ${s} Foot`],
    [`foot_${s}`]: [`toe1-1_${s}`, `Bip01 ${s} Toe0`],
  };
  for (let f = 1; f <= 5; f++) for (let k = 1; k <= 2; k++) d[`finger${f}-${k}_${s}`] = [`finger${f}-${k + 1}_${s}`, `Bip01 ${s} Finger${f - 1}${k}`];
  return d;
}

export interface Rig {
  skeleton: THREE.Skeleton;
  root: THREE.Bone;
  byName: Map<string, THREE.Bone>;
  /** rest world quaternions and positions, by bone name */
  restWorldQ: Map<string, THREE.Quaternion>;
  restWorldP: Map<string, THREE.Vector3>;
}

/** Build the skeleton for the human's current shape (metres, feet on y = 0). */
export function buildRig(human: Human): Rig {
  const rig = human.data.manifest.rig;
  const joint = new Map<string, THREE.Vector3>();
  const tmp = new THREE.Vector3();
  const jointPos = (name: string) => {
    let p = joint.get(name);
    if (p) return p;
    const verts = rig.joints[name] ?? [];
    p = new THREE.Vector3();
    for (const v of verts) p.add(human.vertex(v, tmp));
    p.divideScalar(Math.max(1, verts.length));
    joint.set(name, p);
    return p;
  };

  const byName = new Map<string, THREE.Bone>();
  const worldM = new Map<string, THREE.Matrix4>();
  const restWorldQ = new Map<string, THREE.Quaternion>(), restWorldP = new Map<string, THREE.Vector3>();
  const bones: THREE.Bone[] = [];
  const pending = [...rig.bones];
  // parents before children
  while (pending.length) {
    const i = pending.findIndex((b) => !b.parent || byName.has(safe(b.parent)));
    const b = pending.splice(i < 0 ? 0 : i, 1)[0];
    const head = jointPos(b.head), tail = jointPos(b.tail);
    const y = tail.clone().sub(head);
    if (y.lengthSq() < 1e-10) y.set(0, 1, 0);
    y.normalize();
    // roll: x axis follows the normal of the bone's rotation plane (Blender convention)
    let x = new THREE.Vector3(1, 0, 0);
    const plane = rig.planes[b.rotation_plane];
    if (plane) {
      const [a, bb, c] = plane.map(jointPos);
      const n = bb.clone().sub(a).cross(c.clone().sub(a));
      if (n.lengthSq() > 1e-12) x = n.normalize();
    }
    x.addScaledVector(y, -x.dot(y));
    if (x.lengthSq() < 1e-8) x = Math.abs(y.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1);
    x.normalize();
    const z = new THREE.Vector3().crossVectors(x, y).normalize();
    const world = new THREE.Matrix4().makeBasis(x, y, z).setPosition(head);
    const bone = new THREE.Bone();
    bone.name = safe(b.name);
    const parentWorld = b.parent ? worldM.get(safe(b.parent))! : new THREE.Matrix4();
    bone.matrix.copy(parentWorld.clone().invert().multiply(world));
    bone.matrix.decompose(bone.position, bone.quaternion, bone.scale);
    if (b.parent) byName.get(safe(b.parent))!.add(bone);
    byName.set(bone.name, bone);
    worldM.set(bone.name, world);
    restWorldQ.set(bone.name, new THREE.Quaternion().setFromRotationMatrix(world));
    restWorldP.set(bone.name, head.clone());
    bones.push(bone);
  }
  const root = byName.get('root')!;
  root.updateMatrixWorld(true);
  const skeleton = new THREE.Skeleton(bones);
  return { skeleton, root, byName, restWorldQ, restWorldP };
}

/** Top-4 skin weights for the body's render vertices (from per-source-vertex weights). */
function bodySkin(human: Human, r2s: Uint16Array, rig: Rig): [Uint16Array, Float32Array] {
  const d = human.data;
  const srcIdx = d.view(d.manifest.rig.skinIndex) as Uint8Array;
  const srcW = d.view(d.manifest.rig.skinWeight) as Float32Array;
  const names = d.manifest.rig.bones.map((b) => safe(b.name));
  const boneIndex = new Map(rig.skeleton.bones.map((b, i) => [b.name, i]));
  const remap = names.map((n) => boneIndex.get(n) ?? 0);
  const idx = new Uint16Array(r2s.length * 4), w = new Float32Array(r2s.length * 4);
  for (let i = 0; i < r2s.length; i++) for (let k = 0; k < 4; k++) {
    idx[i * 4 + k] = remap[srcIdx[r2s[i] * 4 + k]];
    w[i * 4 + k] = srcW[r2s[i] * 4 + k];
  }
  return [idx, w];
}

/**
 * A skinned copy of the human in its current shape: body and proxies as SkinnedMeshes bound to a
 * fitted skeleton. The live Human (with its sliders) stays untouched.
 */
export function makeSkinnedHuman(human: Human): { group: THREE.Group; rig: Rig; meshes: THREE.SkinnedMesh[] } {
  const rig = buildRig(human);
  const group = new THREE.Group();
  group.add(rig.root);
  const meshes: THREE.SkinnedMesh[] = [];
  const d = human.data;
  const srcIdx = d.view(d.manifest.rig.skinIndex) as Uint8Array;
  const srcW = d.view(d.manifest.rig.skinWeight) as Float32Array;
  const boneIndex = new Map(rig.skeleton.bones.map((b, i) => [b.name, i]));
  const remap = d.manifest.rig.bones.map((b) => boneIndex.get(safe(b.name)) ?? 0);

  for (const part of human.parts()) {
    const geo = part.mesh.geometry.clone();
    let idx: Uint16Array, w: Float32Array;
    if (part.kind === 'body') {
      [idx, w] = bodySkin(human, part.renderToSrc, rig);
    } else {
      // proxy vertices inherit a blend of the weights of the three body vertices they follow
      const n = part.renderToSrc.length;
      idx = new Uint16Array(n * 4); w = new Float32Array(n * 4);
      const acc = new Map<number, number>();
      for (let i = 0; i < n; i++) {
        acc.clear();
        const pv = part.renderToSrc[i];
        for (let r = 0; r < 3; r++) {
          const bv = part.refs![pv * 3 + r], rw = part.weights![pv * 3 + r];
          if (rw === 0) continue;
          for (let k = 0; k < 4; k++) {
            const bw = srcW[bv * 4 + k];
            if (bw > 0) { const bi = remap[srcIdx[bv * 4 + k]]; acc.set(bi, (acc.get(bi) ?? 0) + bw * rw); }
          }
        }
        const top = [...acc.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
        const tot = top.reduce((s, [, x]) => s + x, 0) || 1;
        top.forEach(([bi, x], k) => { idx[i * 4 + k] = bi; w[i * 4 + k] = x / tot; });
        if (!top.length) { idx[i * 4] = boneIndex.get('head') ?? 0; w[i * 4] = 1; }
      }
    }
    geo.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(idx, 4));
    geo.setAttribute('skinWeight', new THREE.Float32BufferAttribute(w, 4));
    const mesh = new THREE.SkinnedMesh(geo, part.mesh.material as THREE.Material);
    mesh.name = part.mesh.name;
    mesh.castShadow = part.mesh.castShadow;
    mesh.receiveShadow = true;
    mesh.renderOrder = part.mesh.renderOrder;
    mesh.frustumCulled = false;
    mesh.bind(rig.skeleton, new THREE.Matrix4());
    group.add(mesh);
    meshes.push(mesh);
    // (hair's soft layer, the strands' fading ends blended over the solid core: the same skinned shape, its own material)
    const soft = part.mesh.children.find((c) => c.name.endsWith(':soft')) as THREE.Mesh | undefined;
    if (soft) {
      const s = new THREE.SkinnedMesh(geo, soft.material as THREE.Material);
      s.name = soft.name; s.renderOrder = soft.renderOrder; s.frustumCulled = false; s.onBeforeRender = soft.onBeforeRender;
      s.bind(rig.skeleton, new THREE.Matrix4());
      group.add(s);
    }
  }
  wearJewellery(group, human.appearance.jewellery ?? []); // (fitted to this body and what it wears)
  wearHairdo(group, human.appearance.hair, human.appearance.hairColour === 'original' ? '#1a1410' : human.appearance.hairColour);
  return { group, rig, meshes };
}

/**
 * Retarget a Rocketbox clip onto a fitted MakeHuman rig by sampling it on the source skeleton
 * and transferring world-space rotation changes. Rest poses differ (Rocketbox arms out,
 * MakeHuman arms down), so each mapped bone first gets a swing that aligns its rest direction
 * with the source bone's rest direction.
 */
/**
 * (in slices of ~8 ms, yielding to the game between them: the first time the phone came out, retargeting its clip
 * at once held the game for seconds; now the phone opens and the pose follows a moment later. The source skeleton is
 * shared, so callers take turns: anims.ts)
 */
export async function retargetClip(source: THREE.Object3D, clip: THREE.AnimationClip, rig: Rig, fps = 30): Promise<THREE.AnimationClip> {
  const src = new Map<string, THREE.Object3D>();
  source.traverse((o) => src.set(o.name, o));
  const sanitize = (n: string) => THREE.PropertyBinding.sanitizeNodeName(n);
  const getSrc = (n: string) => src.get(n) ?? src.get(sanitize(n));

  // source rest = the scene's own transforms before any animation is applied; they are restored
  // afterwards so the same source can be reused for the next clip
  const saved: [THREE.Object3D, THREE.Vector3, THREE.Quaternion, THREE.Vector3][] = [];
  source.traverse((o) => saved.push([o, o.position.clone(), o.quaternion.clone(), o.scale.clone()]));
  source.updateMatrixWorld(true);
  const srcRestQ = new Map<string, THREE.Quaternion>(), srcRestP = new Map<string, THREE.Vector3>();
  for (const [, s] of Object.entries(RETARGET_MAP)) {
    const o = getSrc(s);
    if (!o) continue;
    srcRestQ.set(s, o.getWorldQuaternion(new THREE.Quaternion()));
    srcRestP.set(s, o.getWorldPosition(new THREE.Vector3()));
  }
  // per-bone alignment swing (target rest direction -> source rest direction)
  const align = new Map<string, THREE.Quaternion>();
  for (const [t, s] of Object.entries(RETARGET_MAP)) {
    const dir = DIRECTION[t];
    const so = getSrc(s), sc = dir && getSrc(dir[1]);
    const tp = rig.restWorldP.get(t), tc = dir && rig.restWorldP.get(dir[0]);
    if (!so || !sc || !tp || !tc) { align.set(t, new THREE.Quaternion()); continue; }
    const sDir = sc.getWorldPosition(new THREE.Vector3()).sub(srcRestP.get(s)!).normalize();
    const tDir = tc.clone().sub(tp).normalize();
    align.set(t, new THREE.Quaternion().setFromUnitVectors(tDir, sDir));
  }

  const mixer = new THREE.AnimationMixer(source);
  const action = mixer.clipAction(clip).play();
  const frames = Math.max(2, Math.round(clip.duration * fps));
  const times = new Float32Array(frames);
  const order = rig.skeleton.bones; // parents before children (built that way)
  const mapped = order.filter((b) => RETARGET_MAP[b.name] && getSrc(RETARGET_MAP[b.name]));
  const values = new Map<string, Float32Array>(mapped.map((b) => [b.name, new Float32Array(frames * 4)]));
  const rootPos = new Float32Array(frames * 3);
  const worldQ = new Map<string, THREE.Quaternion>();
  const pelvis = getSrc('Bip01 Pelvis');
  const hipScale = (rig.restWorldP.get('root')?.y ?? 1) / (srcRestP.get('Bip01 Pelvis')?.y || 1);
  const q = new THREE.Quaternion(), delta = new THREE.Quaternion(), parentQ = new THREE.Quaternion();
  const tmpV = new THREE.Vector3();
  const rigRest = order.map((b) => [b, b.quaternion.clone(), b.position.clone()] as const);
  // (each bone's world rotation read from the matrices updated once a frame: getWorldQuaternion updated every
  // ancestor again for every bone, and the first time the phone came out the retarget held the game for 3 s)
  const invRest = new Map<string, THREE.Quaternion>([...srcRestQ].map(([k, v]) => [k, v.clone().invert()]));
  const scratchP = new THREE.Vector3(), scratchS = new THREE.Vector3();
  const sL = getSrc('Bip01 L Foot'), sR = getSrc('Bip01 R Foot');
  const tL = rig.byName.get('foot_L'), tR = rig.byName.get('foot_R');
  const feet = sL && sR && tL && tR ? {
    srcL: sL, srcR: sR, tgtL: tL, tgtR: tR,
    srcRestL: sL.getWorldPosition(new THREE.Vector3()).y, srcRestR: sR.getWorldPosition(new THREE.Vector3()).y,
    tgtRestL: rig.restWorldP.get('foot_L')!.y, tgtRestR: rig.restWorldP.get('foot_R')!.y,
  } : null;

  let slice = performance.now();
  for (let f = 0; f < frames; f++) {
    if (performance.now() - slice > 8) { await new Promise((r) => setTimeout(r, 0)); slice = performance.now(); }
    const t = (f / frames) * clip.duration;
    times[f] = t;
    mixer.setTime(t);
    source.updateMatrixWorld(true);
    worldQ.clear();
    for (const bone of order) {
      const parentName = bone.parent && (bone.parent as THREE.Bone).isBone ? bone.parent.name : null;
      if (parentName) parentQ.copy(worldQ.get(parentName)!); else parentQ.identity();
      const s = RETARGET_MAP[bone.name];
      const so = s && getSrc(s);
      if (so) {
        so.matrixWorld.decompose(scratchP, q, scratchS);
        delta.copy(q).multiply(invRest.get(s)!); // source change from its rest
        const world = delta.clone().multiply(align.get(bone.name)!).multiply(rig.restWorldQ.get(bone.name)!);
        worldQ.set(bone.name, world);
        const local = parentQ.clone().invert().multiply(world);
        local.toArray(values.get(bone.name)!, f * 4);
      } else {
        worldQ.set(bone.name, parentQ.clone().multiply(bone.quaternion)); // keeps its rest local rotation
      }
    }
    // hips: keep the source's bob and sway (scaled to this body), in place horizontally
    const p = pelvis ? new THREE.Vector3().setFromMatrixPosition(pelvis.matrixWorld) : new THREE.Vector3();
    const rest = rig.restWorldP.get('root')!;
    const dp = p.sub(srcRestP.get('Bip01 Pelvis') ?? p).multiplyScalar(hipScale);
    const rp = new THREE.Vector3(rest.x + dp.x, rest.y + dp.y, rest.z + dp.z);
    // ground it: leg proportions differ, so pose this body and move the hips until the lower
    // foot is as far above its rest height as the source's lower foot is above its own
    if (feet) {
      for (const b of mapped) b.quaternion.fromArray(values.get(b.name)!, f * 4);
      rig.root.position.copy(rp);
      rig.root.updateMatrixWorld(true);
      const lift = (o: THREE.Object3D, restY: number) => tmpV.setFromMatrixPosition(o.matrixWorld).y - restY; // (both skeletons updated just above)
      const want = Math.min(lift(feet.srcL, feet.srcRestL), lift(feet.srcR, feet.srcRestR));
      const have = Math.min(lift(feet.tgtL, feet.tgtRestL), lift(feet.tgtR, feet.tgtRestR));
      rp.y += want - have;
    }
    rootPos.set([rp.x, rp.y, rp.z], f * 3);
  }
  // put the rig back in its rest pose
  for (const [b, q0, p0] of rigRest) { b.quaternion.copy(q0); b.position.copy(p0); }
  rig.root.updateMatrixWorld(true);
  action.stop();
  mixer.uncacheRoot(source);
  for (const [o, p, r, s] of saved) { o.position.copy(p); o.quaternion.copy(r); o.scale.copy(s); }
  source.updateMatrixWorld(true);
  const tracks: THREE.KeyframeTrack[] = [new THREE.VectorKeyframeTrack('root.position', times, rootPos)];
  for (const [name, v] of values) tracks.push(new THREE.QuaternionKeyframeTrack(`${name}.quaternion`, times, v));
  return new THREE.AnimationClip(clip.name, clip.duration, tracks);
}
